/**
 * Returns true when the given date falls on a specific month/day, or within a
 * lead-up window of `leadDays` calendar days before it (inclusive of both the
 * start of the window and the day itself). Useful for annual events / easter
 * eggs that should appear a little early.
 *
 * Examples:
 *   isSpecificDate(4, 1)         -> true only on April 1st.
 *   isSpecificDate(10, 31, 7)    -> true from October 24th through October 31st.
 *
 * The comparison is done on whole calendar days using the local-time getters,
 * so it is evaluated in the user's own timezone. In the browser this means a
 * user in Tokyo flips on April 1st JST while a user in New York flips on April
 * 1st EDT. Do not switch these to the getUTC* variants unless you specifically
 * want a single global UTC-based rollover instead of per-user local dates.
 *
 * Lead windows that cross a year boundary are handled (e.g. a January 1st date
 * with a 7-day lead also matches the preceding December 25th-31st).
 *
 * @param month    1-indexed month (1 = January, 12 = December)
 * @param day      day of the month (1-31)
 * @param leadDays how many days before the date to also return true (default 0)
 * @param now      the date to test; defaults to the current date
 */
export const isSpecificDate = (
  month: number,
  day: number,
  leadDays = 0,
  now: Date = new Date(),
): boolean => {
  // Compare on local midnight so the time of day doesn't matter.
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  // The event recurs every year, so check the occurrence anchored to the
  // previous, current, and next year. This catches windows that straddle a
  // year boundary (e.g. today in late December for a January 1st event).
  for (const year of [
    now.getFullYear() - 1,
    now.getFullYear(),
    now.getFullYear() + 1,
  ]) {
    const target = new Date(year, month - 1, day);
    // Date normalizes out-of-range days, so `day - leadDays` rolls back across
    // month and year boundaries correctly.
    const windowStart = new Date(year, month - 1, day - leadDays);
    if (today >= windowStart && today <= target) {
      return true;
    }
  }
  return false;
};
